import React, { useState } from 'react';
import { updateContactCore, deleteContactCore } from '@gv/core';
import { supabase } from '../../../lib/supabase';
import HistorySection from './HistorySection';
import { Archive as ArchiveIcon, Trash2 as TrashIcon } from 'lucide-react';
import ConfirmDialog from '../../ConfirmDialog';

interface ContactSettingsProps {
    contactId: string;
    userId?: string;
    onBack: () => void;
    showNotification: (message: string, type: 'success' | 'error') => void;
}

export default function ContactSettings({ contactId, userId, onBack, showNotification }: ContactSettingsProps) {
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const handleArchiveContact = async () => {
        if (!contactId) return;
        try {
            await updateContactCore(supabase as any, String(contactId), { is_archived: true as any });
            showNotification('Contact archived', 'success');
            try {
                // Notify list views and persist hidden marker
                window.dispatchEvent(new CustomEvent('gv:contact-removed', { detail: { id: contactId, reason: 'archived' } }));
                const key = 'gv:removedContactIds';
                const raw = sessionStorage.getItem(key);
                const arr = raw ? (JSON.parse(raw) as string[]) : [];
                if (!arr.includes(contactId)) {
                    arr.push(contactId);
                    sessionStorage.setItem(key, JSON.stringify(arr));
                }
            } catch { }
            onBack();
        } catch (e) {
            showNotification('Failed to archive contact', 'error');
        }
    };

    const handleDeleteContact = async () => {
        if (!contactId) return;
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        try {
            await deleteContactCore(supabase as any, String(contactId));
            showNotification('Contact deleted', 'success');
            try {
                window.dispatchEvent(new CustomEvent('gv:contact-removed', { detail: { id: contactId, reason: 'deleted' } }));
                const key = 'gv:removedContactIds';
                const raw = sessionStorage.getItem(key);
                const arr = raw ? (JSON.parse(raw) as string[]) : [];
                if (!arr.includes(contactId)) {
                    arr.push(contactId);
                    sessionStorage.setItem(key, JSON.stringify(arr));
                }
            } catch { }
            setShowDeleteConfirm(false);
            onBack();
        } catch (e) {
            showNotification('Failed to delete contact', 'error');
            setShowDeleteConfirm(false);
        }
    };

    if (!contactId) return null;

    return (
        <>
            <div className="bg-gray-900 rounded-xl">
                <details className="group">
                    {/* Inset the header and remove default outline; only show outline on hover */}
                    <summary className="list-none cursor-pointer flex items-center justify-between px-5 py-4 mx-3 my-3 rounded-lg select-none border border-transparent hover:border-gray-700 hover:bg-gray-800/40 hover:ring-1 hover:ring-gray-600 transition-colors">
                        <div className="flex items-center space-x-2">
                            <h3 className="text-lg font-semibold text-white">Additional Settings</h3>
                        </div>
                        <span className="text-gray-400 group-open:rotate-180 transition-transform">▾</span>
                    </summary>
                    <div className="px-5 pb-5 space-y-6">
                        {/* Archive Section */}
                        <div className="bg-gray-900 rounded-xl">
                            <div className="w-full flex items-center justify-between px-5 py-4 rounded-t-xl">
                                <div className="flex items-center space-x-2">
                                    <ArchiveIcon className="w-5 h-5 text-yellow-400" />
                                    <h3 className="text-lg font-semibold text-white">Archive</h3>
                                </div>
                            </div>
                            <div className="px-5 pb-5">
                                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                                    <p className="text-sm text-gray-400 mb-3">Archive hides this contact from your directory. You can unarchive it later.</p>
                                    <button
                                        type="button"
                                        onClick={handleArchiveContact}
                                        className="inline-flex items-center justify-center px-5 py-2 rounded-md bg-gradient-to-r from-amber-600 to-amber-700 text-white border border-amber-500 hover:from-amber-700 hover:to-amber-800 shadow-lg hover:shadow-xl transition-all duration-200"
                                    >
                                        Archive Contact
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Delete Section */}
                        <div className="bg-gray-900 rounded-xl">
                            <div className="w-full flex items-center justify-between px-5 py-4 rounded-t-xl">
                                <div className="flex items-center space-x-2">
                                    <TrashIcon className="w-5 h-5 text-red-400" />
                                    <h3 className="text-lg font-semibold text-white">Delete</h3>
                                </div>
                            </div>
                            <div className="px-5 pb-5">
                                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                                    <p className="text-sm text-gray-400 mb-3">Delete permanently removes this contact and associated data. This action cannot be undone.</p>
                                    <button
                                        type="button"
                                        onClick={handleDeleteContact}
                                        className="inline-flex items-center justify-center px-5 py-2 rounded-md bg-gradient-to-r from-red-600 to-red-700 text-white border border-red-500 hover:from-red-700 hover:to-red-800 shadow-lg hover:shadow-xl transition-all duration-200"
                                    >
                                        Delete Contact
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* History Section (embedded as-is) */}
                        <HistorySection contactId={contactId} userId={userId || ''} />
                    </div>
                </details>
            </div>
            <ConfirmDialog
                open={showDeleteConfirm}
                title="Delete Contact"
                message={`Permanently delete this contact?\nThis action cannot be undone.`}
                confirmLabel="Delete"
                cancelLabel="Cancel"
                destructive
                onConfirm={confirmDelete}
                onCancel={() => setShowDeleteConfirm(false)}
            />
        </>
    );
}